import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabButton from "../../../../../Components/common/TabButton/TabButton";
import ToggleButton from "../../../../../Components/common/ToggleButton/ToggleButton";
import Content from "../../../../../Components/common/SubmitPopup/Content/Content";
import { fixedTypeVmIds } from "../../../../../Components/common/SubmitPopup/constants";
import Spinner from "../../../../../Components/common/Spinner/Spinner";
import Warning from "../../../../../Components/common/Warning/Warning";
import Backdrop from "../../../../../Components/UI/Backdrop/Backdrop";

import {
  requestGetAutoSubmissionSettings,
  requestSetAutoSubmissionSettings,
  requestResetAutoSubmissionSettings,
} from "../../../../../store/actions/settingsActions";

import {
  AUTO_SUBMISSION_CONTENT_SHOW,
  AUTO_SUBMISSION_MAX_OS,
  AUTO_SUBMISSION_OS_CATALOG,
  defaultOsSettings,
  getAvailableOs,
  getOsName,
} from "./constants";

const SPINNER_SIZE = { width: 22, height: 22 };

// Backend settings for one OS -> Content shape (type/network_traffic as
// single-element arrays for the checkbox inputs). `enabled` is tracked separately.
const buildOsSettings = (src) => {
  const values = { ...defaultOsSettings, ...(src || {}) };
  return {
    type: [values.type],
    network_traffic: [values.network_traffic],
    command_line: values.command_line,
    enviromental_variable: values.enviromental_variable,
    related_password: values.related_password,
    runtime_duration: values.runtime_duration,
    enforse_runtime: values.enforse_runtime,
    enforse_submission: values.enforse_submission,
    memory_dump: values.memory_dump,
    action_script: values.action_script,
  };
};

// Content selectedFiles shape -> backend settings. Every machine the page knows about is
// sent with its configuration; `enabled` is the selection, so a machine that is turned
// off (or no longer running) keeps its settings without taking part in submissions.
const buildBackendSettings = (selectedFiles, osIds, isEnabled) => {
  const file = selectedFiles[0] || {};
  const settings = {};
  osIds.forEach((id) => {
    const os_settings = file[getOsName(id)];
    if (!os_settings) return;
    settings[id] = {
      enabled: isEnabled(id),
      // Linux and macOS always run the automatic evasive analysis, they have no Type selector
      type: fixedTypeVmIds.includes(id) ? "full_triage" : os_settings.type[0],
      network_traffic: os_settings.network_traffic[0],
      command_line: os_settings.command_line,
      enviromental_variable: os_settings.enviromental_variable,
      related_password: os_settings.related_password,
      runtime_duration: os_settings.runtime_duration,
      enforse_runtime: os_settings.enforse_runtime,
      enforse_submission: false, // force submit is no longer user-configurable; always false
      memory_dump: os_settings.memory_dump,
      action_script: os_settings.action_script,
    };
  });
  return settings;
};

const AutoSubmissionSettings = ({ submitType }) => {
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.settings.autoSubmission[submitType]);
  const loading = useSelector((state) => state.settings.autoSubmissionLoading);
  const vms = useSelector((state) => state.dashboard.vms);

  const [enabledOs, setEnabledOs] = useState([]);
  const [activeOs, setActiveOs] = useState("");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([{}]);
  const [showResetWarning, setShowResetWarning] = useState(false);

  useEffect(() => {
    dispatch(requestGetAutoSubmissionSettings(submitType));
  }, [dispatch, submitType]);

  // Machines reported by /submitpopup/vms, limited to the ones auto submission supports.
  // The body only renders this form when at least one of them is running.
  const availableOs = useMemo(() => getAvailableOs(vms), [vms]);

  // Only running machines get a tab; the rest are not shown at all.
  const osList = useMemo(
    () => AUTO_SUBMISSION_OS_CATALOG.filter((os) => availableOs.includes(os.id)),
    [availableOs],
  );

  // The saved status decides whether a tab label looks active: turning the toggle on
  // does not brighten it until the change has actually been saved.
  const savedEnabled = (id) => Boolean(settings?.[id]?.enabled);

  const settingsLoaded = Boolean(settings && Object.keys(settings).length);

  // Everything the form shows comes from the stored settings: the editable values and the
  // status of each machine. Unsaved changes never survive this.
  const resetFromSettings = useCallback(() => {
    const file = {};
    osList.forEach((os) => {
      file[os.name] = buildOsSettings(settings?.[os.id]);
    });
    setSelectedFiles([file]);
    setEnabledOs(Object.keys(settings || {}).filter((id) => settings[id]?.enabled));
  }, [osList, settings]);

  // Re-sync whenever a fetch / save / reset comes back.
  useEffect(() => {
    resetFromSettings();
  }, [resetFromSettings]);

  // The default tab is chosen once, from the stored statuses, so the page does not land on
  // a locked tab while another machine is active. Toggling a status must not move the user
  // to another tab, so the local enabledOs is deliberately not part of this.
  useEffect(() => {
    const names = osList.map((os) => os.name);
    if (tabInitialized && names.includes(activeOs)) return;

    const firstEnabled = osList.find((os) => settings?.[os.id]?.enabled);
    const next = (firstEnabled || osList[0])?.name || "";
    if (next !== activeOs) setActiveOs(next);
    // until the settings arrive the pick above is provisional; lock it in once they do
    if (settingsLoaded) setTabInitialized(true);
  }, [osList, activeOs, settings, settingsLoaded, tabInitialized]);

  const activeOsId = useMemo(
    () => osList.find((os) => os.name === activeOs)?.id,
    [osList, activeOs],
  );
  const isActiveOsEnabled = Boolean(activeOsId && enabledOs.includes(activeOsId));
  const current = useMemo(() => selectedFiles[0]?.[activeOs], [selectedFiles, activeOs]);

  // Mail Gateway network traffic is decided on the backend (VPN availability), so instead of
  // a choice the section explains where the value comes from.
  const networkTrafficNote =
    submitType === "mail_gateway" ? "Automatically assigned based on VPN availability." : "";

  // The machine count is not enforced here: the backend owns that rule and reports it on
  // save, so the toggle always works and the message comes back as a notification.
  const toggleStatusHandler = () => {
    if (!activeOsId) return;
    setEnabledOs((prev) =>
      prev.includes(activeOsId) ? prev.filter((id) => id !== activeOsId) : [...prev, activeOsId],
    );
  };

  const selectTabHandler = (name) => {
    if (!osList.some((os) => os.name === name)) return;
    // switching tabs shows what is stored, so anything left unsaved is dropped
    resetFromSettings();
    setActiveOs(name);
  };

  const setField = (field, value) => {
    setSelectedFiles((prev) => {
      const file = { ...prev[0] };
      file[activeOs] = { ...file[activeOs], [field]: value };
      return [file];
    });
  };

  // Single-select for the type / network_traffic checkbox groups.
  const checkboxInputChangeHandler = (e) => {
    setField(e.target.name, [e.target.value]);
  };

  const otherInputsChangeHandler = (e) => {
    const field = e.target.name;
    const value = field === "runtime_duration" ? Number(e.target.value) : e.target.value;
    setField(field, value);
  };

  const enforceRuntimeChangeHandler = (oppositeState) => {
    setField("enforse_runtime", oppositeState);
  };

  const saveHandler = () => {
    // machines that are no longer running are still stored, but always as disabled
    const osIds = Array.from(
      new Set([...osList.map((os) => os.id), ...Object.keys(settings || {})]),
    );
    dispatch(
      requestSetAutoSubmissionSettings({
        submitType,
        settings: buildBackendSettings(selectedFiles, osIds, (id) =>
          availableOs.includes(id) ? enabledOs.includes(id) : false,
        ),
      }),
    );
  };

  const resetHandler = () => {
    setShowResetWarning(false);
    dispatch(requestResetAutoSubmissionSettings(submitType));
  };

  return (
    <>
      <div className="button-block auto-submission__tabs">
        {osList.map((os) => (
          <TabButton
            key={os.id}
            title={os.name}
            className={savedEnabled(os.id) ? "" : "auto-submission__tab--inactive"}
            onClick={selectTabHandler}
            active={activeOs}
          />
        ))}
      </div>

      <div className="auto-submission__status">
        <h3 className="auto-submission__status__title">{activeOs}</h3>
        <div className="auto-submission__status__toggle">
          <span>Status</span>
          <ToggleButton active={isActiveOsEnabled} onClick={toggleStatusHandler} />
        </div>
        <p className="auto-submission__status__description">
          Up to {AUTO_SUBMISSION_MAX_OS} operating systems can be active at the same time. A status
          change is only stored once you save; switching tabs restores the saved values.
        </p>
      </div>

      {/* the backdrop covers this whole block, so Reset / Save stay reachable and a machine
          can still be turned off and saved */}
      <div className="auto-submission__content">
        <div className="submit-popup auto-submission__popup">
          <div className="submit-popup-container">
            {current ? (
              <Content
                currentFileIndex={0}
                activeOs={activeOs}
                selectedFiles={selectedFiles}
                show={AUTO_SUBMISSION_CONTENT_SHOW}
                networkTrafficNote={networkTrafficNote}
                onCheckboxInputChange={checkboxInputChangeHandler}
                onEnforceRuntimeChange={enforceRuntimeChangeHandler}
                onOtherInputsChange={otherInputsChangeHandler}
              />
            ) : null}
          </div>
        </div>
        <Backdrop
          spinner={false}
          absolute
          show={Boolean(current) && !isActiveOsEnabled}
          message={`${activeOs} is inactive! Please click on the status button to activate it.`}
          messageStyle={{ paddingTop: 0 }}
        />
      </div>

      <div className="auto-submission__save">
        <button className="m-r-15 default-button" onClick={() => setShowResetWarning(true)}>
          Reset
        </button>
        <button className="primary-button" disabled={!osList.length} onClick={saveHandler}>
          {loading ? <Spinner width={SPINNER_SIZE.width} height={SPINNER_SIZE.height} /> : "Save"}
        </button>
      </div>

      <Warning
        onClickOk={resetHandler}
        show={showResetWarning}
        setShow={setShowResetWarning}
        text="The analysis settings on this page will be reset to their default values. Do you want to continue?"
        article="Reset"
      />
    </>
  );
};

export default AutoSubmissionSettings;
